import {
  FC,
  Fragment,
  memo,
  ReactElement,
  useEffect,
} from 'react';
import parse from 'html-react-parser';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { jsonLdScriptProps } from 'react-schemaorg';

import { Header } from 'src/widgets/Header';
import Footer from 'components/Footer';
import { MainLayoutInterface } from 'components/declarations';
import { Header as HeaderType } from 'src/__generated__/graphqlTypes';
import {
  renderExternalContentfulTag,
  renderExternalScripts,
  renderInlineScripts,
} from 'lib/tag.service';
import { buildGraphSchema } from 'lib/schema.service';

export const MainLayout: FC<MainLayoutInterface> = memo(({
  isDesktopRequested,
  children,
  metaData,
  header,
  isSlugMatchPath,
  footer,
  fonts,
  desktopViewportWidth,
  isMobileHeaderNavActive,
  externalScripts,
  externalStyles,
  externalTags,
}) => {
  const router = useRouter();
  const base = process.env.BASE_SERVER_URL || process.env.NEXT_PUBLIC_BASE_SERVER_URL;
  const canonicalUrl = `${base}${metaData.url || router.asPath}`;

  const applyLayout = (content: ReactElement) => (
    <>
      {isSlugMatchPath
        && externalScripts.map(renderExternalScripts)}
      <Head>
        <link
          rel="preload"
          href="/_next/static/media/Inter-400.9ceec35519bf9404e874cfb7d91083f7.woff2"
          as="font"
          type="font/woff2"
          crossOrigin=""
        />
        <link rel="preconnect" href="https://images.ctfassets.net" />
        {fonts?.map((font) => font.fullText && parse(font.fullText.trim()))}
        {isDesktopRequested
          ? <meta name="viewport" content={`width=${desktopViewportWidth}`} />
          : <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no" />}
        {externalStyles.map((style) => (
          // @ts-ignore
          <Fragment key={style.key}>
            {style}
          </Fragment>
        ))}
        {metaData && (
          <>
            <title>
              {metaData.title}
            </title>
            {metaData.favicon && <link rel="icon" href={metaData.favicon} />}
            <link rel="canonical" href={canonicalUrl} />
            {metaData.redirect && <meta httpEquiv="refresh" content={`0; url = ${metaData.redirect}`} />}
            {metaData.robotMetaValue && <meta name="robots" content={metaData.robotMetaValue} key="robots" />}
            {metaData.description && <meta name="description" content={metaData.description} key="description" />}
            <meta property="og:url" content={canonicalUrl} />
            <meta name="twitter:card" content="summary" />
            {metaData.type && <meta property="og:type" content={metaData.type} key="ogtype" />}
            {metaData.title && <meta property="og:title" content={metaData.title} key="ogtitle" />}
            {metaData.description && <meta property="og:description" content={metaData.description} key="ogdescription" />}
            {metaData.image.url && <meta property="og:image:url" content={metaData.image.url} key="ogimageurl" />}
            {metaData.siteName && <meta property="og:site_name" content={metaData.siteName} key="ogsitename" />}
            {metaData.locale && <meta property="og:locale" content={metaData.locale} key="oglocale" />}
            {metaData.title && <meta name="twitter:title" content={metaData.title} key="twtitle" />}
            {metaData.description && <meta name="twitter:description" content={metaData.description} key="twdesc" />}
            {metaData.image.url && <meta name="twitter:image" content={metaData.image.url} key="twimage" />}
            {metaData.image.contentType && <meta property="og:image:type" content={metaData.image.contentType} key="ogimagetype" />}
            {metaData.image.width && <meta property="og:image:width" content={`${metaData.image.width}`} key="ogimagewidth" />}
            {metaData.image.height && <meta property="og:image:height" content={`${metaData.image.height}`} key="ogimageheight" />}
            {metaData.schema && (
              // eslint-disable-next-line react/jsx-props-no-spreading
              <script {...jsonLdScriptProps<any>(buildGraphSchema(metaData.schema))} />
            )}
            {isSlugMatchPath
              && externalScripts.map(renderInlineScripts)}
          </>
        )}
        <meta name="MobileOptimized" content="width" />
        <meta content="yes" name="apple-mobile-web-app-capable" />
        <meta charSet="utf-8" />
        {isSlugMatchPath && externalTags.map(renderExternalContentfulTag)}
      </Head>
      {content}
    </>
  );

  useEffect(() => {
    if (isMobileHeaderNavActive) {
      document.body.style.overflowY = 'hidden';
    } else {
      // @ts-ignore
      document.body.style.overflowY = null;
    }
  }, [isMobileHeaderNavActive]);

  const content = (
    <>
      { header && <Header header={header as HeaderType} /> }
      { children }
      { footer && <Footer footer={footer} /> }
    </>

  );

  return applyLayout(content);
});
